import type { BrokerConfig } from './schema';
import { ALLOWED_BRAND_TOKENS, VALID_MODES } from './schema';

const TOKEN_CSS_MAP: Record<string, string> = {
  brandAccent: '--brand-accent',
  brandSurfaceInverted: '--brand-surface-inverted',
  brandWordmarkColor: '--brand-wordmark-color',
};

export async function fetchConfig(): Promise<BrokerConfig> {
  const res = await fetch('/config.json');
  if (!res.ok) throw new Error(`CONFIG_FETCH_FAILED:${res.status}`);
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    throw new Error('CONFIG_PARSE_ERROR');
  }
  return validateConfig(raw);
}

export function validateConfig(raw: unknown): BrokerConfig {
  if (!raw || typeof raw !== 'object') throw new Error('CONFIG_INVALID_SHAPE');
  const c = raw as Record<string, unknown>;

  // instance
  if (!c.instance || typeof c.instance !== 'object') throw new Error('CONFIG_MISSING_INSTANCE');
  const inst = c.instance as Record<string, unknown>;
  if (!VALID_MODES.includes(inst.mode as never)) throw new Error('CONFIG_INVALID_MODE');
  if (typeof inst.name !== 'string') throw new Error('CONFIG_MISSING_INSTANCE_NAME');
  if (typeof inst.slug !== 'string') throw new Error('CONFIG_MISSING_INSTANCE_SLUG');

  // auth
  if (!c.auth || typeof c.auth !== 'object') throw new Error('CONFIG_MISSING_AUTH');
  const auth = c.auth as Record<string, unknown>;
  if (typeof auth.enabled !== 'boolean') throw new Error('CONFIG_INVALID_AUTH_ENABLED');
  if (auth.enabled && !auth.keycloak) throw new Error('CONFIG_AUTH_MISCONFIGURED');

  // catalog
  if (!c.catalog || typeof c.catalog !== 'object') throw new Error('CONFIG_MISSING_CATALOG');
  const cat = c.catalog as Record<string, unknown>;
  if (!Array.isArray(cat.tiles)) throw new Error('CONFIG_INVALID_TILES');

  // brandTokens — strip unknown keys
  if (c.brandTokens && typeof c.brandTokens === 'object') {
    const bt = c.brandTokens as Record<string, unknown>;
    for (const key of Object.keys(bt)) {
      if (!ALLOWED_BRAND_TOKENS.includes(key as never)) {
        console.error(`[config] Rejected locked brand token override: ${key}`);
        delete bt[key];
      }
    }
  }

  return raw as BrokerConfig;
}

export function applyBrandTokens(config: BrokerConfig): void {
  if (!config.brandTokens) return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(config.brandTokens)) {
    const cssVar = TOKEN_CSS_MAP[key];
    if (cssVar && typeof value === 'string') {
      root.style.setProperty(cssVar, value);
    }
  }
}
